import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-8c47a962"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "title auto-address-header" }, "Please enter the full address or eircode of the property", -1))
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "label" }, " ", -1))
const _hoisted_3 = { class: "address-container" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "title" }, "Property Address", -1))

import { onMounted } from "vue";
import { displayQuestionAlert } from "@/alerts/questionAlert";
import AutoAddressWrapper from "@/components/ui/address/auto-address-wrapper.vue";
import AcquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import scrollView from "@/components/ui/scroll-view.vue";
import { FieldSize } from "@/enums/system/FieldSize";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { Address } from "@/types/generic/Address";
import { NewRecord } from "@/types/generic/NewRecord";
import { PropertiesValidation } from "acquaint-api/validation/PropertiesValidation";
import { PropType, computed, inject, ref } from "vue";
import { Properties } from "acquaint-api/models/Properties";
import { mapPropertyToConcatenatedString } from "@/utils/properties/PropertyAddressMapper";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { CountryCode } from "bright-logic-core/src/CountryCode";
import { TextboxValueChangeEvent } from "@/enums/ui/inputs/TextboxValueChangeEvent";


export default /*#__PURE__*/_defineComponent({
  __name: 'property-lookup-page',
  props: {
  /**
   * The model value representing the address object.
   * Used with `v-model` for two-way binding.
   * @type {Address}
   * @required
   */
  modelValue: {
    type: Object as PropType<Address>,
    required: true,
  },

  /**
   * The validation group identifier for managing form validations.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    default: "",
  },

  /**
   * The unique identifier for the property.
   * @type {number}
   * @default -1
   */
  propertyId: {
    type: Number,
    default: -1,
  },
},
  emits: [
  "update:modelValue",
  "propertySelected",
  "choseNotToUseExistingProperty",
],
  setup(__props, { emit }) {

const props = __props;





const address = computed({
  get: () => props.modelValue,
  set: (value: Address) => emit("update:modelValue", value),
});

onMounted(() => {
  if (props.propertyId <= 0) {
    return;
  }

  autoFillPropertyFields();
});

const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const propertyData = ref<Properties>();

const fetchPropertyDetails = async () => {
  try {
    propertyData.value = await apiClient.httpGet(
      `database/properties/${props.propertyId}`,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    propertyData.value = undefined;
  }
};

const autoFillPropertyFields = async () => {
  await fetchPropertyDetails();

  if (propertyData.value) {
    address.value.propertyName = propertyData.value.propertyName;
    address.value.street = propertyData.value.street;
    address.value.locality = propertyData.value.locality;
    address.value.town = propertyData.value.town;
    address.value.region = propertyData.value.region;
    address.value.country = propertyData.value.country;
    address.value.countryCode = propertyData.value.countryCode;
    address.value.postCode = propertyData.value.postcode;
    emit("propertySelected", propertyData.value);
  }
};

const onAddressFound = async (e: Address) => {
  address.value = e;

  // To avoid creating duplicate property records, check if the address has been used previously
  const existingProperty = await matchAddressToExistingProperty(e);

  if (!existingProperty) {
    emit("propertySelected");
    return;
  }

  // As the matching process may retrieve the wrong property, we always need to ask the user for confirmation
  if (await askUserToUseExistingProperty(existingProperty)) {
    emit("propertySelected", existingProperty);
  } else {
    emit("choseNotToUseExistingProperty");
  }
};

/** Attempts to match the found address to an existing property
 * @param address the Address model used in matching process
 * @returns Property model populated with matched data */
const matchAddressToExistingProperty = async (
  address: Address,
): Promise<Properties | undefined> => {
  const response = await apiClient.httpPost<NewRecord>(
    "app/utilityswitching/addresslookup/match/property",
    address,
  );

  if (!response.id) {
    return;
  }

  return await apiClient.httpGet<Properties>(
    `database/properties/${response.id}`,
  );
};

/** Displays a prompt asking the user if they would like to use the matched property
 * @param property Existing Property record
 * @returns Boolean indicating whether the user wishes to use the matched property */
const askUserToUseExistingProperty = async (property: Properties) => {
  const message =
    "An existing property has been found with a matching address. \n\n" +
    mapPropertyToConcatenatedString(property) +
    "\n\nWould you like to reuse this property?";

  const promptResult = await displayQuestionAlert(message);

  return promptResult.isConfirmed;
};

const screenWidth = ref(screen.width);

const onSearchCompleted = () => {
  resetScroll.value = true;
};

const resetScroll = ref(false);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(scrollView, {
    "reset-scroll": resetScroll.value,
    "onUpdate:resetScroll": _cache[7] || (_cache[7] = ($event: any) => ((resetScroll).value = $event))
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: _normalizeClass(["property-lookup-container", { smallerDevices: screenWidth.value < 875 }])
      }, [
        _createElementVNode("section", null, [
          _hoisted_1,
          _hoisted_2,
          _createVNode(AutoAddressWrapper, {
            onOnAddressFound: _cache[0] || (_cache[0] = (e) => onAddressFound(e)),
            onOnSearchCompleted: onSearchCompleted
          })
        ]),
        _createElementVNode("section", _hoisted_3, [
          _hoisted_4,
          _createVNode(AcquaintTextbox, {
            modelValue: address.value.propertyName,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((address.value.propertyName) = $event)),
            label: "Property Name",
            fieldValidation: _unref(PropertiesValidation).PropertyName,
            validationGroup: props.validationGroup,
            requiredInput: false,
            width: _unref(FieldSize).Large,
            "value-change-event": _unref(TextboxValueChangeEvent).Change
          }, null, 8, ["modelValue", "fieldValidation", "validationGroup", "width", "value-change-event"]),
          _createVNode(AcquaintTextbox, {
            modelValue: address.value.street,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((address.value.street) = $event)),
            label: "Street",
            validationGroup: props.validationGroup,
            fieldValidation: _unref(PropertiesValidation).Street,
            requiredInput: true,
            width: _unref(FieldSize).Large,
            "value-change-event": _unref(TextboxValueChangeEvent).Change
          }, null, 8, ["modelValue", "validationGroup", "fieldValidation", "width", "value-change-event"]),
          _createVNode(AcquaintTextbox, {
            modelValue: address.value.locality,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((address.value.locality) = $event)),
            label: "Locality",
            validationGroup: props.validationGroup,
            fieldValidation: _unref(PropertiesValidation).Locality,
            requiredInput: false,
            width: _unref(FieldSize).Large,
            "value-change-event": _unref(TextboxValueChangeEvent).Change
          }, null, 8, ["modelValue", "validationGroup", "fieldValidation", "width", "value-change-event"]),
          _createVNode(AcquaintTextbox, {
            modelValue: address.value.town,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((address.value.town) = $event)),
            fieldValidation: _unref(PropertiesValidation).Town,
            label: "Town",
            validationGroup: props.validationGroup,
            requiredInput: true,
            width: _unref(FieldSize).Large,
            "value-change-event": _unref(TextboxValueChangeEvent).Change
          }, null, 8, ["modelValue", "fieldValidation", "validationGroup", "width", "value-change-event"]),
          _createVNode(AcquaintTextbox, {
            modelValue: address.value.region,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((address.value.region) = $event)),
            fieldValidation: _unref(PropertiesValidation).Region,
            label: "County",
            validationGroup: props.validationGroup,
            requiredInput: false,
            width: _unref(FieldSize).Large,
            "value-change-event": _unref(TextboxValueChangeEvent).Change
          }, null, 8, ["modelValue", "fieldValidation", "validationGroup", "width", "value-change-event"]),
          _createVNode(AcquaintTextbox, {
            modelValue: address.value.postCode,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((address.value.postCode) = $event)),
            fieldValidation: _unref(PropertiesValidation).Postcode,
            label: "Eircode",
            validationGroup: props.validationGroup,
            width: _unref(FieldSize).Large,
            "country-code": _unref(CountryCode).Ireland,
            "value-change-event": _unref(TextboxValueChangeEvent).Change
          }, null, 8, ["modelValue", "fieldValidation", "validationGroup", "width", "country-code", "value-change-event"])
        ])
      ], 2)
    ]),
    _: 1
  }, 8, ["reset-scroll"]))
}
}

})